import {
  Box,
  Drawer,
  Stack,
  Divider,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface HistoryData {
  type: string;
  username: string;
  registered_at: string;
  data: any;
}

interface HistoryDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  data: HistoryData | null;
}

const HistoryDetailDrawer = ({
  open,
  onClose,
  data,
}: HistoryDetailDrawerProps) => {
  const translateType = (type: string) => {
    switch (type) {
      case "fopag_termination":
        return "Rescisão";
      case "fopag_vacation":
        return "Férias";
      case "fopag_absence":
        return "Atestado/Afastamento";
      case "ticket":
        return "Chamado";
      default:
        return type || "Não identificado";
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatRequestData = (type: string, requestData: any) => {
    if (!requestData) return {};

    const fieldMappings: { [key: string]: { [key: string]: string } } = {
      fopag_vacation: {
        period_init_date: "Inicio do período aquisitivo",
        vacation_init_date: "Inicio do período de férias",
        days: "Dias",
        advance_13o: "Antecipar 13º",
        allowance_days: "Abono pecuniário",
        observations: "Observações",
        name: "Nome",
      },
      fopag_termination: {
        type: "Tipo de rescisão",
        reason: "Motivo",
        date: "Data",
        obs: "Observações",
        name: "Nome",
      },
      fopag_absence: {
        type: "Tipo",
        start_date: "Data Início",
        end_date: "Data Fim",
        days: "Quantidade de Dias",
        is_work_accident: "Acidente de trabalho",
        had_previous_absence: "Afastamentos nos últimos 60 dias",
        observations: "Observações",
        name: "Nome",
      },
      ticket: {
        "ticket.subject": "Tipo",
        "ticket.comment.body": "Assunto",
      },
    };

    const mappings = fieldMappings[type] || {};
    const formattedData: { [key: string]: any } = {};

    if (type === "ticket" && requestData.ticket) {
      formattedData["Tipo"] = requestData.ticket.subject;

      const commentBody = requestData.ticket.comment.body;
      const dateMatch = commentBody.match(
        /Data de expectativa: (\d{1,2}\/\d{1,2}\/\d{4})/,
      );

      formattedData["Assunto"] = commentBody.replace(
        /\s*-\s*Data de expectativa: \d{1,2}\/\d{1,2}\/\d{4}$/,
        "",
      );

      if (dateMatch && dateMatch[1]) {
        formattedData["Data de Expectativa"] = dateMatch[1];
      }

      return formattedData;
    }

    Object.entries(requestData).forEach(([key, value]) => {
      if (key === "reference") return;

      const shouldFormatDate =
        key === "period_init_date" ||
        key === "vacation_init_date" ||
        (type === "fopag_termination" && key === "date") ||
        (type === "fopag_absence" &&
          (key === "start_date" || key === "end_date"));

      const formattedValue = shouldFormatDate
        ? formatDate(value as string)
        : value;

      const label = mappings[key] || key;
      formattedData[label] = formattedValue;
    });

    return formattedData;
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ p: 3, pt: 2, width: 400 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center">
            <Stack direction="row" sx={{ flex: 1 }} alignItems="center">
              <Typography variant="body1">Solicitação</Typography>
            </Stack>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Stack spacing={2} divider={<Divider />}>
            <Stack direction="row">
              <Typography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
                Tipo
              </Typography>
              <Typography variant="caption" sx={{ flex: 1 }}>
                {data ? translateType(data.type) : ""}
              </Typography>
            </Stack>

            <Stack direction="row">
              <Typography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
                Requerente
              </Typography>
              <Typography variant="caption" sx={{ flex: 1 }}>
                {data?.username || "N/A"}
              </Typography>
            </Stack>

            <Stack direction="row">
              <Typography variant="caption" fontWeight="bold" sx={{ flex: 1 }}>
                Data
              </Typography>
              <Typography variant="caption" sx={{ flex: 1 }}>
                {data ? formatDate(data.registered_at) : ""}
              </Typography>
            </Stack>
          </Stack>

          <Divider />

          <Typography variant="body1" fontWeight="bold">
            Dados da Solicitação
          </Typography>

          <Divider />

          <Stack spacing={2} divider={<Divider />}>
            {Object.entries(
              formatRequestData(data?.type || "", data?.data),
            ).map(([key, value]) => (
              <Stack key={key} direction="row">
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ flex: 1 }}
                >
                  {key}
                </Typography>
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {value?.toString() || "-"}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default HistoryDetailDrawer;
